import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { Button } from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import { useMfaVerify } from "@/hooks/useMfa";
import { authService } from "@/services/authService";
import { extractErrorMessage } from "@/services/axiosConfig";
import "./LoginPage.css";

export function LoginPage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const mfaVerify = useMfaVerify();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // When the backend answers login with `{ mfa_required: true }`, the session is
  // not yet emitted — we switch to the TOTP step and submit the 2nd factor.
  const [mfaStep, setMfaStep] = useState(false);
  const [code, setCode] = useState("");

  if (!loading && user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { mfa_required } = await authService.login(email.trim(), password);
      if (mfa_required) {
        setMfaStep(true);
        return;
      }
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await mfaVerify.mutateAsync(code.trim());
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  // Recovery path: o desafio de MFA expira (~5 min) ou pode falhar repetidamente.
  // Voltar limpa o estado do 2o fator e devolve o usuario ao login, que reinicia
  // o desafio numa nova autenticacao.
  function backToLogin() {
    setMfaStep(false);
    setCode("");
    setPassword("");
    setError(null);
  }

  if (mfaStep) {
    return (
      <div className="login-shell">
        <form className="login-card" onSubmit={onVerify}>
          <h1 className="login-title">Verificação em dois fatores</h1>
          <p className="login-sub">
            Digite o código gerado pelo seu aplicativo autenticador.
          </p>

          {error && (
            <div className="login-error">
              {error}
              <span className="login-error-hint">
                Se o código não funcionar, o desafio pode ter expirado — volte e
                entre novamente.
              </span>
            </div>
          )}

          <label className="login-field">
            <span>Código</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              required
              autoFocus
            />
          </label>

          <Button
            type="submit"
            variant="primary"
            disabled={mfaVerify.isPending || code.length !== 6}
          >
            {mfaVerify.isPending ? "Verificando…" : "Verificar"}
          </Button>
          <button type="button" className="login-link" onClick={backToLogin}>
            Voltar para o login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={onSubmit}>
        <h1 className="login-title">Tributo Devido</h1>
        <p className="login-sub">Entre com suas credenciais para continuar.</p>

        {error && <div className="login-error">{error}</div>}

        <label className="login-field">
          <span>E-mail</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </label>
        <label className="login-field">
          <span>Senha</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? "Entrando…" : "Entrar"}
        </Button>
        <Link to="/forgot-password" className="login-link">
          Esqueci a senha
        </Link>
        <Link to="/privacidade/solicitar" className="login-link login-link-muted">
          Solicitação de privacidade (LGPD)
        </Link>
      </form>
    </div>
  );
}
